/**
 * Teacher Bulk Tutor Analytics API client.
 * Uses the gateway axios client with interceptors for auth/tenant headers.
 */

import { createHttpClient } from '../../api/http';
import { serviceUrls } from '../../config/serviceUrls';
import type {
  TeacherStudentsSummaryOut,
  TeacherStudentsSummaryParams,
} from '../types/teacherTutorAnalytics';

const client = createHttpClient(serviceUrls.gateway);

/**
 * Build query params: use start_date/end_date when both set; otherwise use days.
 * Only include params that are defined. Never send only one of start_date/end_date.
 */
function buildQueryParams(params: TeacherStudentsSummaryParams): Record<string, string | number> {
  const q: Record<string, string | number> = {};

  const hasCustomRange =
    params.start_date != null &&
    params.start_date !== '' &&
    params.end_date != null &&
    params.end_date !== '';

  if (hasCustomRange) {
    q.start_date = params.start_date!;
    q.end_date = params.end_date!;
  } else if (params.days != null) {
    q.days = params.days;
  }

  if (params.student_id != null && params.student_id !== '') q.student_id = params.student_id;
  if (params.class_id != null && params.class_id !== '') q.class_id = params.class_id;
  if (params.grade != null && params.grade !== '') q.grade = params.grade;
  if (params.skip != null) q.skip = params.skip;
  if (params.limit != null) q.limit = params.limit;

  return q;
}

/**
 * Get bulk tutor analytics for all students accessible to the teacher.
 * Accepts optional filters and pagination; default is days-only (backward compatible).
 */
export async function getTeacherStudentsTutorSummary(
  params: TeacherStudentsSummaryParams
): Promise<TeacherStudentsSummaryOut> {
  const queryParams = buildQueryParams(params);
  const response = await client.get(`/v1/tutor/analytics/teacher/me/students/summary`, {
    params: queryParams,
  });
  return response.data;
}